import type { ColumnDef } from '@ics-portal/ics-data-table';
import {
    ComponentCreateRequestStatusCode,
    type GetComponentCreateRequestsPagedQuery,
} from '@gql/graphql';
import { humanizeRequestStatus } from '@pages/notification-components/utils/humanizeRequestStatus';
import { StatusCell } from './components/StatusCell';
import { UserCell } from './components/UserCell';

export type RequestRow =
    GetComponentCreateRequestsPagedQuery['componentCreateRequestsView']['edges'][number]['node'];

const PRIORITY_LABELS: Record<string, string> = {
    LOW: 'Low',
    MEDIUM: 'Medium',
    HIGH: 'High',
};

const STATUS_OPTIONS = Object.values(ComponentCreateRequestStatusCode).map(
    (code) => ({ value: code, label: humanizeRequestStatus(code) }),
);

export const REQUEST_COLUMNS: ColumnDef<RequestRow>[] = [
    {
        field: 'componentName',
        header: 'Name',
        filter: 'text',
        filterKey: 'componentName',
        sortKey: 'COMPONENTNAME',
    },
    {
        field: 'statusCode',
        header: 'Status',
        filter: 'set',
        filterKey: 'hasComponentCreateRequestStatusWith.code',
        filterOptions: STATUS_OPTIONS,
        render: (ctx) => (
            <StatusCell id={ctx.row.id} status={ctx.row.statusCode} />
        ),
    },
    {
        field: 'componentVersion',
        header: 'Version',
        filter: 'text',
        filterKey: 'componentVersion',
        sortKey: 'COMPONENTVERSION',
    },
    {
        field: 'componentVendorName',
        header: 'Vendor',
        hidden: true,
        filter: 'text',
        filterKey: 'componentVendorName',
        sortKey: 'COMPONENTVENDORNAME',
    },
    {
        field: 'requestPriority',
        header: 'Priority',
        hidden: true,
        type: 'enum',
        labels: PRIORITY_LABELS,
        filter: 'set',
        filterKey: 'requestPriority',
        filterOptions: Object.entries(PRIORITY_LABELS).map(
            ([value, label]) => ({ value, label }),
        ),
        sortKey: 'REQUESTPRIORITY',
    },
    {
        field: 'externalRequestStatus',
        header: 'Ext. request status',
        hidden: true,
        filter: 'text',
        filterKey: 'externalRequestStatus',
        sortKey: 'EXTERNALREQUESTSTATUS',
    },
    {
        field: 'externalComponentID',
        header: 'Ext. component ID',
        hidden: true,
        filter: 'text',
        filterKey: 'externalComponentID',
        sortKey: 'EXTERNALCOMPONENTID',
    },
    {
        field: 'id',
        header: 'Request ID',
        hidden: true,
        filter: 'text',
        filterKey: 'id',
    },
    {
        field: 'requesterID',
        header: 'Requester',
        hidden: true,
        filter: 'text',
        filterKey: 'requesterID',
        render: (ctx) => <UserCell userId={ctx.row.requesterID} />,
    },
    {
        field: 'referenceWeb',
        header: 'Website URL',
        hidden: true,
        filter: 'text',
        filterKey: 'referenceWeb',
        sortKey: 'REFERENCEWEB',
    },
    {
        field: 'referenceSecurity',
        header: 'Security URL',
        hidden: true,
        filter: 'text',
        filterKey: 'referenceSecurity',
        sortKey: 'REFERENCESECURITY',
    },
    {
        field: 'componentID',
        header: 'Component ID',
        hidden: true,
        filter: 'text',
        filterKey: 'componentID',
        sortKey: 'COMPONENTID',
    },
    {
        field: 'componentVendorID',
        header: 'Vendor ID',
        hidden: true,
        filter: 'text',
        filterKey: 'componentVendorID',
        sortKey: 'COMPONENTVENDORID',
    },
    {
        field: 'statusID',
        header: 'Status ID',
        hidden: true,
        filter: 'text',
        filterKey: 'statusID',
        sortKey: 'STATUSID',
    },
    {
        field: 'sourceID',
        header: 'Source ID',
        hidden: true,
        filter: 'text',
        filterKey: 'sourceID',
        sortKey: 'SOURCEID',
    },
    {
        field: 'externalRequestRef',
        header: 'External request ref',
        hidden: true,
        filter: 'text',
        filterKey: 'externalRequestRef',
        sortKey: 'EXTERNALREQUESTREF',
    },
];
